import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { checkCustomerDiscount } from '@theme/customer-price-check';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    // Every card on the page calls this within the same short window, and
    // customer-price-check.js batches all of it into one request instead of
    // one per card — see its module doc for why that matters here.
    this.applyCustomerDiscount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Sync the data this component needs to re-check the customer's
    // discount for whichever variant is now selected.
    if (newProductPrice instanceof HTMLElement) {
      this.dataset.variantId = newProductPrice.dataset.variantId;
      this.dataset.minQuantity = newProductPrice.dataset.minQuantity;
    }
    // A freshly server-rendered container never carries the "resolved"
    // marker, so the global CSS rule hides it again immediately — no need
    // to toggle anything here before the check runs.
    this.applyCustomerDiscount(newPrice instanceof HTMLElement ? newPrice : undefined);
  };

  /**
   * Checks whether the signed-in customer has an automatic discount on the
   * currently selected variant and, if so, overlays it on the displayed
   * price. See customer-price-check.js for why this requires a live check
   * rather than reading it straight from Liquid.
   *
   * The price starts hidden via a global CSS rule keyed off the
   * data-customer-logged-in attribute already present in the server-rendered
   * HTML (see base.css), so there's no gap between first paint and this
   * script running where the regular price could flash. Once the check
   * resolves — found a discount or not — this marks the container so that
   * CSS rule stops hiding it.
   * @param {HTMLElement} [container] - The price container to mark resolved. Defaults to the current ref.
   */
  async applyCustomerDiscount(container = this.refs.priceContainer) {
    if (this.dataset.customerLoggedIn !== 'true') return;

    const variantId = this.dataset.variantId;
    const minQuantity = Number(this.dataset.minQuantity) || 1;
    // A campaign price (compare-at set in Admin) is never discounted further
    // for the customer — see snippets/price.liquid. Read from `container`,
    // which updatePrice() passes as the new variant's freshly rendered one.
    const isCampaignSale = container?.hasAttribute('data-campaign-sale') ?? false;

    try {
      if (!variantId) return;

      const result = await checkCustomerDiscount(variantId, minQuantity, { isCampaignSale });
      if (!result) return;

      // The customer may have switched variants again while this was in
      // flight — don't apply a stale result to the wrong variant.
      if (this.dataset.variantId !== variantId) return;

      const { priceContainer } = this.refs;
      if (!priceContainer) return;

      // Skip the DOM write entirely if this is the same result already
      // displayed. This component participates in a page-wide "something
      // changed, re-verify everything" watcher (see the bottom of this
      // file) precisely because morph doesn't reliably notify every patched
      // card on its own — but rendering the discount always mutates the
      // DOM, which would otherwise retrigger that same watcher and loop
      // forever. A morph resetting this card to its server-rendered
      // default also clears this fingerprint along with everything else,
      // so a genuine change still re-renders correctly.
      const fingerprint = `${result.discountedCents}:${result.originalCents}:${result.discountTitle ?? ''}`;
      if (priceContainer.dataset.appliedDiscount === fingerprint) return;
      priceContainer.dataset.appliedDiscount = fingerprint;

      this.renderDiscountedPrice(priceContainer, result);
    } finally {
      // Re-read the ref rather than reusing `container`: by now the
      // MutationObserver has long since caught up, and the container may
      // have been replaced again since we started.
      (this.refs.priceContainer ?? container)?.classList.add('price-discount-resolved');
    }
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {{discountedCents: number, originalCents: number, discountTitle: string | null}} discount
   */
  renderDiscountedPrice(priceContainer, { discountedCents, originalCents, discountTitle }) {
    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (!format || !currency) return;

    const priceEl = priceContainer.querySelector('.price');
    if (!priceEl) return;

    const discountedText = formatMoney(discountedCents, format, currency);
    const percentageOff = Math.round(((originalCents - discountedCents) / originalCents) * 100);
    const hadCompareAt = !!priceContainer.querySelector('.compare-at-price');
    const saleLabel = this.dataset.labelSale ?? '';

    priceContainer.querySelector('.price-discount-percentage')?.remove();
    priceContainer.querySelector('.price-discount-title')?.remove();

    // Update the price element in place rather than replacing it, so
    // whatever styling classes it already carries in this context (e.g. a
    // homepage card's own price class) are preserved.
    priceEl.innerHTML = hadCompareAt
      ? discountedText
      : `<span class="visually-hidden">${saleLabel}&nbsp;</span>${discountedText}`;

    priceEl.insertAdjacentHTML('afterend', `<span class="price-discount-percentage">-${percentageOff}%</span>`);

    if (!hadCompareAt) {
      const regularLabel = this.dataset.labelRegular ?? '';
      const originalText = formatMoney(originalCents, format, currency);
      const compareEl = document.createElement('span');
      compareEl.className = 'compare-at-price';
      compareEl.innerHTML = `<span class="visually-hidden">${regularLabel}&nbsp;</span>${originalText}`;
      priceEl.before(compareEl);
    }

    if (discountTitle) {
      priceContainer.insertAdjacentHTML(
        'beforeend',
        `<div class="price-discount-title">${discountTitle}</div>`
      );
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

// Filter/sort results on a collection page are morphed into the existing
// grid (see assets/section-renderer.js) rather than inserted as fresh
// elements — an existing card slot gets patched to show a different
// product. In principle that should be catchable per-element (morph.js
// calls the framework's own updatedCallback() hook on every Component it
// patches, and does call plain setAttribute() on the live node), but in
// practice neither reliably fires for every single patched card, and this
// is customer-facing pricing — worth being unconditionally correct about
// rather than depending on precisely which code path morph took for a
// given card.
//
// So: watch the whole page for any DOM change, and once things settle,
// re-verify every product-price on it. This doesn't cost anything for
// prices that are already correct — checkCustomerDiscount() (see
// customer-price-check.js) caches every answer, so re-checking an
// unchanged variant resolves instantly from cache with no network request.
const REVERIFY_DEBOUNCE_MS = 200;
/** @type {ReturnType<typeof setTimeout> | null} */
let reverifyTimer = null;

function reverifyAllPrices() {
  reverifyTimer = null;
  for (const element of document.querySelectorAll('product-price[data-customer-logged-in="true"]')) {
    if (element instanceof ProductPrice) element.applyCustomerDiscount();
  }
}

if (typeof document !== 'undefined') {
  new MutationObserver(() => {
    if (reverifyTimer !== null) clearTimeout(reverifyTimer);
    reverifyTimer = setTimeout(reverifyAllPrices, REVERIFY_DEBOUNCE_MS);
  }).observe(document.body, { childList: true, subtree: true });
}
